/** Совпадает с `min(var(--messenger-dm-bubble-max-width), 85%)` у бабла в ленте (tokens.css). */
export function readMessengerDmBubbleMaxWidthPx(fromEl?: HTMLElement | null): number {
  const el =
    fromEl ??
    (typeof document !== 'undefined' ? (document.documentElement as HTMLElement | null) : null);
  if (!el) return 450;
  const raw = getComputedStyle(el).getPropertyValue('--messenger-dm-bubble-max-width').trim();
  if (raw.endsWith('px')) {
    const n = parseFloat(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 450;
}

/** Читает пиксельный лимит из CSS-переменной обёртки (см. `.chatAssistant__userMsgImageWrap` в MessengerDmMessages.css). */
export function readMessengerInlinePhotoMaxHeightPx(el: HTMLElement): number {
  const raw = getComputedStyle(el).getPropertyValue('--messenger-inline-photo-max-h').trim();
  if (raw.endsWith('px')) {
    const n = parseFloat(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return Math.min(window.innerHeight * 0.5, 420);
}

/** Читает минимальную сторону слота (px) из CSS-переменной — при достижении включает cover-режим. */
export function readMessengerInlinePhotoMinSidePx(el: HTMLElement): number {
  const raw = getComputedStyle(el).getPropertyValue('--messenger-inline-photo-min-side').trim();
  if (raw.endsWith('px')) {
    const n = parseFloat(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  // fallback: 180px
  return 180;
}

/**
 * Размер слота под inline-фото с поддержкой cover-режима (min-side).
 *
 * Алгоритм:
 * 1. Сначала вычисляет contain-размеры (как раньше).
 * 2. Если contain-ширина < minSidePx — форсирует ширину = minSidePx,
 *    высоту пропорционально (cover по вертикали — crop верх/низ).
 * 3. Если contain-высота < minSidePx — форсирует высоту = minSidePx,
 *    ширину пропорционально (cover по горизонтали — crop края).
 * 4. cover = true когда слот больше contain-размера в любом направлении.
 *    Компонент использует object-fit: cover вместо contain.
 */
export function computeMessengerInlinePendingSlotPx(opts: {
  columnMaxWidth: number;
  imageWidth: number;
  imageHeight: number;
  maxHeightPx: number;
  minSidePx: number;
}): { widthPx: number; heightPx: number; cover: boolean } {
  const { columnMaxWidth, imageWidth, imageHeight, maxHeightPx, minSidePx } = opts;
  if (columnMaxWidth <= 0 || imageWidth <= 0 || imageHeight <= 0 || maxHeightPx <= 0) {
    return { widthPx: 0, heightPx: 0, cover: false };
  }

  // 1. contain-размеры (downscale только, upscale не делаем)
  const scale = Math.min(columnMaxWidth / imageWidth, maxHeightPx / imageHeight, 1);
  let w = Math.round(imageWidth * scale);
  let h = Math.round(imageHeight * scale);
  let cover = false;

  // 2. Если ширина меньше minSide — форсируем ширину (crop по вертикали)
  if (minSidePx > 0 && w < minSidePx) {
    w = minSidePx;
    h = Math.round(minSidePx * imageHeight / imageWidth);
    cover = true;
    // Если после форсирования высота превышает maxHeight — обрезаем (всё равно cover)
    if (h > maxHeightPx) {
      h = maxHeightPx;
    }
  }

  // 3. Если высота меньше minSide — форсируем высоту (crop по горизонтали)
  if (minSidePx > 0 && h < minSidePx) {
    h = minSidePx;
    w = Math.round(minSidePx * imageWidth / imageHeight);
    cover = true;
    // Если после форсирования ширина превышает columnMax — обрезаем (всё равно cover)
    if (w > columnMaxWidth) {
      w = columnMaxWidth;
    }
  }

  return { widthPx: w, heightPx: h, cover };
}
